import React from 'react';
import {Button, Flex, Tooltip} from "antd";
import DirectLayoutInputPopup from "../../../../Modal/Modal-Content/DirectLayoutInputPopup";
import jsPDF, {AcroFormTextField} from "jspdf";
import directLayoutBaseImage from "../../../../../../../assets/img/report/direct-layout/Direct_Layout_Base_Image.png";
import {useViewerStore} from '../../../../../../../store/store';
import {logger} from '../../../../../../../services/loggingService';
import {downloadDirectLayout} from '../../../../../../../services/documentService';
import {isDirectLayoutAutomateEnabled} from '../../../../../../../services/userCapabilityService';
import {DownloadIcon} from "@phosphor-icons/react";
import {useAreChangesMadeToDesign} from "../../../../../../../hooks/useAreChangesMadeToDesign";
import {
    useProductionCalculationReady,
    useSiteCalculationBlockedStatus,
} from "../../../../../../../hooks/calculationStateHook";
import {useCurrentUser} from "../../../../../../../contexts/UserContext";
import {ViewerState} from "../../../../../../../@types/store";
import {AlertType} from "../../../../../../../@types/enums";

function DirectLayoutPDFGenerator() {

    const sceneViewer = useViewerStore(state => state.sceneViewer);
    const {hasCapability} = useCurrentUser();
    const {areChangesMade, isOriginalSite} = useAreChangesMadeToDesign();
    const loadingCompleted = useViewerStore((state) => state.loadingCompleted);
    const refreshingShade = useViewerStore((state) => state.refreshingShade);
    const shadeCalculatingIdx = useViewerStore((state) => state.shadeCalculatingIdx);
    const shadeCalculatingLength = useViewerStore((state) => state.shadeCalculatingLength);
    const productionValuesReady = useProductionCalculationReady();
    const productionBlockedStatus = useSiteCalculationBlockedStatus({
        loadingCompleted,
        refreshingShade,
        productionValuesReady,
        shadeCalculatingIdx: shadeCalculatingIdx + 1,
        shadeCalculatingLength,
    });
    const usesOneDocDirectLayout = () =>
        hasCapability("Direct_Layout_Automate") || isDirectLayoutAutomateEnabled();
    const waitingForProduction = !usesOneDocDirectLayout() && productionBlockedStatus != null;
    const directLayoutTooltip = areChangesMade
        ? "Changes were made. Finalize before downloading."
        : isOriginalSite
            ? "Feature disabled for the Original design. Select a finalized design."
            : waitingForProduction
                ? productionBlockedStatus
                : "";


    const openSiteReportPopup = () => {
        useViewerStore.setState({isLeftSidebarOpen: false});
        sceneViewer.viewerAlert({
            show: true,
            title: "Download Direct Layout Report",
            message: <DirectLayoutInputPopup/>,
            messageType: "info",
            isModal: true
        })

    }

    const downloadDirectLayoutFromOneDoc = async () => {
        useViewerStore.setState({isLeftSidebarOpen: false});
        sceneViewer.viewerAlert({
            show: true,
            title: "Downloading Direct Layout Report",
            message:
                <Flex justify={'center'} align={'center'} gap={10}>
                    <span className="loader-icon-spinner"></span>
                    <p>It might take some time, please wait</p>
                </Flex>,
            messageType: AlertType.info,
            isModal: true,
        });

        try {
            const oppSfId = sessionStorage.getItem('SalesForceId') ?? '';
            const blob = await downloadDirectLayout(oppSfId);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `oneDRAW_Direct_Layout_${oppSfId}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            sceneViewer.viewerAlert({show: false});
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Direct Layout download failed';
            sceneViewer.viewerAlert({
                show: true,
                title: "Error",
                message:
                    <Flex justify={'center'} align={'center'} gap={10}>
                        <p>We could not download the Direct Layout file.<br/>{message}</p>
                    </Flex>,
                messageType: AlertType.error,
                isModal: true,
            });
        }
    }

    const handleGenerateDirectLayout = () => {
        if (usesOneDocDirectLayout()) {
            void downloadDirectLayoutFromOneDoc();
            return;
        }
        openSiteReportPopup();
    };


    return (
        <Tooltip title={directLayoutTooltip}>
            <Button
                disabled={areChangesMade || isOriginalSite || waitingForProduction}
                icon={<DownloadIcon size={20}/>}
                id={"openDirectLayoutFormBtn"}
                type='primary'
                size='large'
                block
                onClick={handleGenerateDirectLayout}
            >
                Generate Direct Layout
            </Button>
        </Tooltip>
    );
}

export default DirectLayoutPDFGenerator;

export const generateDirectLayout  = async (viewerState : ViewerState, usageInput : number | null, productionInput : number | null) : Promise<jsPDF> => {
    const {
        sceneViewer,
        totalSystemSize,
        actualWattage,
        activeModule,
        siteAddress: address,
        customerSiteRestrictions,
        selectedLossesVendor,
    } = viewerState;

    const lossesVendorLabel =
        selectedLossesVendor === 'OneDraw'
            ? 'oneDRAW'
            : (selectedLossesVendor ?? 'oneDRAW');
    return new Promise(async (resolve, reject) => {
        try {

            const totalModuleCount = sceneViewer.moduleMeshArr.length;


            // Define custom page dimensions (in millimeters)
            const customPageWidth = 210; // Width of the custom page
            const customPageHeight = 297; // Height of the custom page

            const pdf = new jsPDF({
                unit: 'mm', format: [customPageWidth, customPageHeight], // Set custom page size
            });

            // Calculate the width and height of the page in mm
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Add the first image to fill the entire custom-sized page
            pdf.addImage(directLayoutBaseImage, 'PNG', 0, 0, customPageWidth, customPageHeight, "DirectLayoutBaseImage", 'FAST');

            // Calculate the maximum width for the address
            const maxAddressWidth = 64; // Adjust this value as needed

            // Function to split text into lines that fit within a given width
            function splitText(text : string, maxWidth : number) {
                const words = text.split(' ');
                let line = '';
                const lines = [];

                for (const word of words) {
                    const testLine = line + (line === '' ? '' : ' ') + word;
                    const testWidth = pdf.getStringUnitWidth(testLine) * (pdf.getFontSize() / pdf.internal.scaleFactor);

                    if (testWidth > maxWidth) {
                        lines.push(line);
                        line = word;
                    } else {
                        line = testLine;
                    }
                }

                lines.push(line); // Add the last line
                return lines;
            }

            // Split the address into lines that fit within the maxAddressWidth
            const addressLines = splitText(address, maxAddressWidth);

            // Calculate the X position to center the combined lines
            const textAboveX = pageWidth - 176;

            const opportunityName = splitText(String(sessionStorage.getItem('opportunityName')), maxAddressWidth);

            //Calculate Y position for opportunity name line
            const opportunityNameLineY = 78;

            //Set font size to 10, text color to black, then add text for opportunity name
            pdf.setFontSize(10);
            pdf.setTextColor(0, 0, 0);
            pdf.text(opportunityName, textAboveX, opportunityNameLineY,{align : 'center'});

            // Calculate the Y position for the address lines
            const addressLineY = opportunityNameLineY + 10; // Adjust for vertical positioning
            var addressLineYLAst = 0
            // Loop through and add the address lines
            pdf.setFontSize(10);
            pdf.setTextColor(0, 0, 0); // Set text color to black
            addressLines.forEach((line, index) => {
                pdf.text(line, textAboveX, addressLineY + index * 4, {align: 'center'});
                addressLineYLAst = addressLineY + index * 4;
            });


            const textAboveLine4 = 'Annual Usage';
            const textAboveLine5 = usageInput + ' kWh';
            const textAboveLine6 = 'Service Provided by:';
            const textAboveLine7 = sessionStorage.getItem('Utility_Company');
            // Calculate the maximum width for textAboveLine7
            const maxTextAboveLine7Width = 40;

            // Split textAboveLine7 into lines that fit within the maxTextAboveLine7Width
            const textAboveLine7Lines = splitText(String(textAboveLine7), maxTextAboveLine7Width);

            const textAboveLine4Y = addressLineYLAst + 8;
            const textAboveLine5Y = textAboveLine4Y + 4;
            const textAboveLine6Y = textAboveLine5Y + 6;
            const textAboveLine7Y = textAboveLine6Y + 4;


            pdf.setFontSize(10);
            pdf.setTextColor(0, 0, 0); // Set text color to black
            pdf.text(textAboveLine4, textAboveX, textAboveLine4Y, {align: 'center'});
            pdf.text(textAboveLine5, textAboveX, textAboveLine5Y, {align: 'center'});
            pdf.text(textAboveLine6, textAboveX, textAboveLine6Y, {align: 'center'});
            textAboveLine7Lines.forEach((line, index) => {
                pdf.text(line, textAboveX, textAboveLine7Y + index * 4, {align: 'center'});
            });


            // Aerial: height capped to template window; width uses full slot (uniform scale narrowed both sides)
            const secondImageY = 56.5;
            const secondImageSlotMaxBottomY = 169;
            const secondImageSlotWidthRef = 135.2;
            const secondImageSlotLeftRef = 67;
            const secondImageHeight = secondImageSlotMaxBottomY - secondImageY;
            const secondImageWidth = secondImageSlotWidthRef;
            const secondImageX = secondImageSlotLeftRef;

            const homeImage = await sceneViewer.getImageForDirectLayout();

            pdf.addImage(homeImage, 'PNG', secondImageX, secondImageY, secondImageWidth, secondImageHeight, "DirectLayoutHomeImage", 'FAST');

            const restrictionNotes = String(customerSiteRestrictions ?? '').trim();

            // Calculate the X position to center the combined lines
            const textBelowX = pageWidth - 176; // Adjust for right positioning

            // Add the next text in black color
            const textBelow1 = `${totalSystemSize.toFixed(2)} kW System`;

            // Module line — 10 pt to match System Details body
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            // Split module text into multiple lines to fit box
            const moduleLineY = 157;
            var moduleLineYLast = 0;
            const moduleLines = splitText(`${totalModuleCount} ${activeModule.text} Modules`, 40);
            moduleLines.forEach((line, index) => {
                pdf.text(line, textBelowX, moduleLineY + index * 4,{align: 'center'});
                moduleLineYLast = moduleLineY + index * 4;
            });

            if (!usageInput || !productionInput) {
                productionInput = 0;
                usageInput = 0;
            }

            const textBelowEstProd = `Est Production: ${productionInput} kWh`;
            const textBelowLossesVendor = `Losses Vendor: ${lossesVendorLabel}`;

            const offset = Math.round((productionInput / usageInput) * 100);
            let textBelowUsageOffset;
            if (isNaN(offset) || offset === Infinity) {
                textBelowUsageOffset = 'Usage Offset: N/A';
            } else {
                textBelowUsageOffset = 'Usage Offset: ' + offset + '%';
            }

            // Adobe Sign signature field — must sit on the Customer Approval line, not in the restrictions text area above it.
            const adobeSignTextField = new AcroFormTextField();
            adobeSignTextField.multiline = true;
            adobeSignTextField.width = 110;
            adobeSignTextField.height = 8;
            adobeSignTextField.x = 17;
            adobeSignTextField.y = 204;
            adobeSignTextField.fieldName = "Sig_es_:signer2:signature";
            adobeSignTextField.color = '#000000';
            adobeSignTextField.textAlign = 'center';
            pdf.addField(adobeSignTextField);

            //text size to 
            pdf.setFontSize(10);


            const systemDetailsLineStep = 5.5;
            const textBelowLine1Y = 135;
            const textBelowLine2Y = textBelowLine1Y + systemDetailsLineStep;
            const textBelowLine3Y = textBelowLine2Y + systemDetailsLineStep;
            const textBelowLine4Y = textBelowLine3Y + systemDetailsLineStep;

            pdf.setTextColor(0, 0, 0);
            pdf.text(textBelow1, textBelowX, textBelowLine1Y, {align: 'center'});
            pdf.text(textBelowEstProd, textBelowX, textBelowLine2Y, {align: 'center'});
            pdf.text(textBelowLossesVendor, textBelowX, textBelowLine3Y, {align: 'center'});
            pdf.text(textBelowUsageOffset, textBelowX, textBelowLine4Y, {align: 'center'});

            // Customer / site restrictions — draw AFTER system details (title on artwork). Old Y (~157.5) overlapped System Details / module line (~158).
            const restrictionTextX = 13;
            // Must match the left content column on the template (~annual benefits box starts ~130 mm). Full-page width (e.g. 197 mm) prevents splitTextToSize from wrapping.
            const restrictionContentRightMm = 128;
            const restrictionMaxWidthMm = Math.max(30, restrictionContentRightMm - restrictionTextX - 2);
            const restrictionStartY = 186;
            const restrictionMaxY = 200;
            //line height to 3.35
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            pdf.setTextColor(0, 0, 0);
            if (restrictionNotes.length > 0) {
                const wrappedRestrictionLines = pdf.splitTextToSize(
                    restrictionNotes,
                    restrictionMaxWidthMm,
                );
                let restrictionLineY = restrictionStartY;
                const restrictionLineStep = 3.75;
                for (let ri = 0; ri < wrappedRestrictionLines.length; ri++) {
                    if (restrictionLineY > restrictionMaxY) {
                        break;
                    }
                    pdf.text(wrappedRestrictionLines[ri], restrictionTextX, restrictionLineY);
                    restrictionLineY += restrictionLineStep;
                }
            }

            const noOfBulbs = Math.ceil((actualWattage / 117))
            const noOfTrees = Math.ceil((actualWattage / 55))
            const noOfTons = Math.ceil((actualWattage * 18 / 2000))

            // Add the next text in green color
            const textBelowA = `${noOfBulbs} Bulbs`;
            const textBelowB = `${noOfTrees} Trees`;
            const textBelowC = `${noOfTons} Tons`;

            // Calculate the X position to center the combined lines
            const textBelowAX = pageWidth - 30; // Adjust for right positioning

            //increase text size to 12
            pdf.setFontSize(12);

            // Calculate the Y position for the lines
            const textBelowLineAY = 208; // Adjust for vertical positioning
            const textBelowLineBY = textBelowLineAY + 16;
            const textBelowLineCY = textBelowLineBY + 16;

            pdf.setTextColor(19, 197, 19); // Set text color to black
            pdf.text(textBelowA, textBelowAX, textBelowLineAY, {align: 'center'});
            pdf.text(textBelowB, textBelowAX, textBelowLineBY, {align: 'center'});
            pdf.text(textBelowC, textBelowAX, textBelowLineCY, {align: 'center'});
            // pdf = new Blob([pdf], {type: 'application/pdf'});
            resolve(pdf);
        } catch (error) {
            logger.error(error, 'Error generating Direct Layout Reports');
            reject(false);
        }

    })


};
